import { useState, useEffect, useRef, useCallback, useContext } from 'react'
import { Wheel } from 'spin-wheel'

import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { checkPrizeEligibility, savePrizeSpin } from '../service/prizeSpinService'
import { PRIZE_WHEEL_CONFIG, PRIZE_WHEEL_COLORS } from '../constants/prizeConfig'

import '../css/post-survey.scss'

export default function PrizeWheelModal({ isOpen, onClose }) {
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext)

  const [isChecking, setIsChecking] = useState(true)
  const [isEligible, setIsEligible] = useState(false)
  const [alreadySpun, setAlreadySpun] = useState(false)
  const [isSpinning, setIsSpinning] = useState(false)
  const [winner, setWinner] = useState(null)
  const [showResult, setShowResult] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [userEmail, setUserEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [overlayImage, setOverlayImage] = useState(null)

  const wheelContainerRef = useRef(null)
  const wheelRef = useRef(null)

  // Load overlay image
  useEffect(() => {
    const img = new Image()
    img.onload = () => setOverlayImage(img)
    img.onerror = () => setOverlayImage(null)
    img.src = '/images/prizeWheel/wheel-overlay.svg'
  }, [])

  // Check eligibility on open
  useEffect(() => {
    if (!isOpen || !currentUser?.id || !session?.id) return

    const check = async () => {
      setIsChecking(true)
      try {
        const result = await checkPrizeEligibility(currentUser.id, session.id)
        if (result.alreadySpun) {
          setIsEligible(false)
          setAlreadySpun(true)
        } else {
          setIsEligible(result.eligible)
        }
      } catch (err) {
        console.error('Eligibility check failed:', err)
        setIsEligible(false)
      } finally {
        setIsChecking(false)
      }
    }

    check()
  }, [isOpen, currentUser?.id, session?.id])

  // Handle wheel rest
  const handleWheelRest = useCallback(async (event) => {
    setIsSpinning(false)
    if (event.currentIndex !== undefined) {
      const winningItem = PRIZE_WHEEL_CONFIG[event.currentIndex]
      const prize = winningItem.label || "Unknown"
      setWinner(prize)
      setShowResult(true)

      // Auto-save TRY AGAIN results
      if (prize.includes("TRY AGAIN") && currentUser?.id && session?.id) {
        try {
          await savePrizeSpin(currentUser.id, session.id, prize)
        } catch (err) {
          console.error('Auto-save failed:', err)
        }
      }
    }
  }, [currentUser?.id, session?.id])

  // Initialize wheel
  useEffect(() => {
    if (isOpen && isEligible && !showResult && wheelContainerRef.current && !wheelRef.current && overlayImage !== null) {
      try {
        wheelRef.current = new Wheel(wheelContainerRef.current, {
          items: PRIZE_WHEEL_CONFIG,
          radius: 0.84,
          itemLabelRadius: 0.93,
          itemLabelRadiusMax: 0.35,
          itemLabelRotation: 180,
          itemLabelAlign: 'left',
          itemLabelColors: ['#fff'],
          itemLabelBaselineOffset: -0.07,
          itemLabelFont: 'Arial Black, Arial, sans-serif',
          itemLabelFontSizeMax: 28,
          itemBackgroundColors: PRIZE_WHEEL_COLORS,
          rotationSpeedMax: 500,
          rotationResistance: -100,
          lineWidth: 1,
          lineColor: '#fff',
          borderWidth: 3,
          borderColor: '#000',
          overlayImage: overlayImage,
          pointerAngle: 90,
          isInteractive: false,
          onRest: handleWheelRest
        })
      } catch (err) {
        console.error('Wheel init failed:', err)
      }
    }

    return () => {
      if (wheelRef.current) {
        try {
          wheelRef.current.remove()
          wheelRef.current = null
        } catch (err) {
          console.error('Wheel cleanup error:', err)
        }
      }
    }
  }, [isOpen, isEligible, showResult, handleWheelRest, overlayImage])

  const handleSpin = () => {
    if (!wheelRef.current || isSpinning) return
    setIsSpinning(true)
    const randomRotation = Math.random() * 360 + 360 * 3
    wheelRef.current.spinTo(randomRotation, 3000)
  }

  const handleClaim = async () => {
    const isWin = winner && !winner.includes("TRY AGAIN")

    if (isWin) {
      if (!userEmail.trim()) {
        setEmailError('Email is required to claim your prize')
        return
      }
      if (!userEmail.includes('@vt.edu')) {
        setEmailError('Please enter a valid VT email address')
        return
      }
      setEmailError('')
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      await savePrizeSpin(currentUser.id, session.id, winner, isWin ? userEmail : null)
      onClose()
    } catch (err) {
      setSaveError('Failed to save. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  const isWin = winner && !winner.includes("TRY AGAIN")

  // Loading
  if (isChecking) {
    return (
      <div className="survey-overlay">
        <div className="survey-modal survey-thankyou">
          <p style={{ fontSize: '14px', color: '#6b7280' }}>Checking eligibility...</p>
        </div>
      </div>
    )
  }

  // Already spun
  if (alreadySpun) {
    return (
      <div className="survey-overlay">
        <div className="survey-modal survey-thankyou">
          <div className="survey-check">✓</div>
          <h2>Already Spun!</h2>
          <p>You've already spun the wheel for this session.</p>
          <div className="survey-footer" style={{ justifyContent: 'center' }}>
            <button className="survey-submit" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    )
  }

  // Not eligible
  if (!isEligible) {
    return (
      <div className="survey-overlay">
        <div className="survey-modal survey-thankyou">
          <h2>Unable to access prize wheel</h2>
          <div className="survey-footer" style={{ justifyContent: 'center' }}>
            <button className="survey-submit" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="survey-overlay">
      <div className="survey-modal" style={{ width: '700px' }}>
        {!showResult ? (
          <>
            <div className="survey-header" style={{ textAlign: 'center' }}>
              <h2>Time to Spin!</h2>
              <p>Thank you for your participation. Claim your free spin!</p>
            </div>

            <div className="survey-questions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 32px' }}>
              <div
                ref={wheelContainerRef}
                style={{
                  width: '440px',
                  height: '440px',
                  marginBottom: '24px',
                  filter: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.15))'
                }}
              />

              <button
                className="survey-submit"
                onClick={handleSpin}
                disabled={isSpinning}
                style={{
                  padding: '12px 40px',
                  fontSize: '16px',
                  fontWeight: 600,
                  opacity: isSpinning ? 0.6 : 1
                }}
              >
                {isSpinning ? 'Spinning...' : 'Spin!'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="survey-header" style={{ textAlign: 'center' }}>
              <h2>{isWin ? 'Congratulations!' : 'Thanks for Participating!'}</h2>
              <p>{isWin ? 'You won a prize!' : 'You\'ll have another chance to spin at the next session.'}</p>
            </div>

            {isWin ? (
              <div className="survey-questions" style={{ padding: '20px 32px' }}>
                {saveError && (
                  <div className="survey-error">{saveError}</div>
                )}

                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  background: '#f0fdf4',
                  border: '1.5px solid #86efac',
                  borderRadius: '10px',
                  marginBottom: '20px'
                }}>
                  <p style={{ fontSize: '20px', fontWeight: 600, color: '#166534', margin: 0 }}>{winner}</p>
                </div>

                <div className="survey-question" style={{ borderBottom: 'none', paddingTop: '0' }}>
                  <label>Enter your VT email to claim your prize:</label>
                  <span className="survey-subtitle">We'll contact you within 24-48 hours with details.</span>
                  <input
                    type="email"
                    className="survey-text-input"
                    value={userEmail}
                    onChange={(e) => { setUserEmail(e.target.value); setEmailError('') }}
                    placeholder="pid@vt.edu"
                    style={emailError ? { borderColor: '#f87171' } : {}}
                  />
                  {emailError && (
                    <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                      {emailError}
                    </p>
                  )}
                </div>
              </div>
            ) : null}

            <div className="survey-footer">
              <button
                className="survey-submit"
                onClick={handleClaim}
                disabled={isSaving}
                style={{ opacity: isSaving ? 0.6 : 1 }}
              >
                {isSaving ? 'Saving...' : isWin ? 'Claim Prize' : 'Done'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}